import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  Image as ImageIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { createChecklistItem, reorderItems } from "../utils/checklistUtils";

/**
 * ChecklistEditor - Edit checklist name and items
 * @param {Object} checklist - Checklist object
 * @param {Function} onUpdate - Update handler
 * @param {Function} onDelete - Delete handler
 * @param {Function} onCancel - Cancel handler
 * @param {boolean} readOnly - Read-only mode
 */
const ChecklistEditor = ({ checklist, onUpdate, onDelete, onCancel, readOnly = false }) => {
  const [name, setName] = useState(checklist.name);
  const [items, setItems] = useState(checklist.items || []);
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemType, setNewItemType] = useState("checkbox");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [errors, setErrors] = useState({});

  // Handle add new item
  const handleAddItem = () => {
    if (!newItemLabel.trim()) {
      setErrors({ newItem: "Item label is required" });
      return;
    }

    const newItem = createChecklistItem(newItemType, newItemLabel.trim());
    setItems([...items, newItem]);
    setNewItemLabel("");
    setNewItemType("checkbox");
    setErrors({});
  };

  // Handle delete item
  const handleDeleteItem = (itemId) => {
    setItems(items.filter((item) => item.id !== itemId));
  };

  // Handle item label change
  const handleItemLabelChange = (itemId, newLabel) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, label: newLabel } : item
      )
    );
  };

  // Handle drag start
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const reordered = reorderItems(items, draggedIndex, index);
    setItems(reordered);
    setDraggedIndex(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Handle save
  const handleSave = () => {
    // Validate
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Checklist name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Update checklist
    onUpdate({
      ...checklist,
      name: name.trim(),
      items,
    });
    onCancel();
  };

  // Handle delete checklist
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${checklist.name}"?`)) {
      onDelete();
    }
  };

  // Get icon for item type
  const getItemTypeIcon = (type) => {
    switch (type) {
      case "checkbox":
        return <CheckBoxIcon fontSize="small" />;
      case "image_required":
        return <ImageIcon fontSize="small" />;
      case "comment_required":
        return <CommentIcon fontSize="small" />;
      default:
        return <CheckBoxIcon fontSize="small" />;
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Edit Checklist
          </Typography>

          {/* Checklist name */}
          <TextField
            fullWidth
            label="Checklist Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={readOnly}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Items list */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Items ({items.length})
          </Typography>

          {items.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No items yet. Add your first item below.
            </Alert>
          ) : (
            <List>
              {items.map((item, index) => (
                <ListItem
                  key={item.id}
                  draggable={!readOnly}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: draggedIndex === index ? "action.hover" : "background.paper",
                    cursor: readOnly ? "default" : "move",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {/* Drag handle */}
                  {!readOnly && (
                    <IconButton size="small" sx={{ cursor: "move", mr: 1 }}>
                      <DragIndicatorIcon />
                    </IconButton>
                  )}

                  {/* Item type icon */}
                  <Box sx={{ mr: 2 }}>{getItemTypeIcon(item.type)}</Box>

                  {/* Item label */}
                  <TextField
                    fullWidth
                    size="small"
                    value={item.label}
                    onChange={(e) => handleItemLabelChange(item.id, e.target.value)}
                    disabled={readOnly}
                    sx={{ mr: 1 }}
                  />

                  {/* Item type chip */}
                  <Chip
                    label={item.type.replace("_", " ")}
                    size="small"
                    sx={{ textTransform: "capitalize", mr: 1 }}
                  />

                  {/* Delete button */}
                  {!readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Add new item */}
        {!readOnly && (
          <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Item
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Item Type</InputLabel>
                <Select
                  value={newItemType}
                  label="Item Type"
                  onChange={(e) => setNewItemType(e.target.value)}
                >
                  <MenuItem value="checkbox">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckBoxIcon fontSize="small" />
                      Checkbox
                    </Box>
                  </MenuItem>
                  <MenuItem value="image_required">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ImageIcon fontSize="small" />
                      Image Required
                    </Box>
                  </MenuItem>
                  <MenuItem value="comment_required">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CommentIcon fontSize="small" />
                      Comment Required
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Item Label"
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                error={!!errors.newItem}
                helperText={errors.newItem}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddItem();
                  }
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                sx={{ minWidth: 100 }}
              >
                Add
              </Button>
            </Box>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={readOnly}
          >
            Delete Checklist
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<CancelIcon />} onClick={onCancel}>
              Cancel
            </Button>
            {!readOnly && (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                Save
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChecklistEditor;
